<template>
  <component
    :is="component"
    :href="href"
    :to="to"
    :target="target || fallbackTarget"
    :rel="[rel, relAdditional].filter((r) => !!r).join(' ') || null"
    :data-component="`Link[${componentName}]`"
    @click="onClick"
  >
    <slot name="iconLeft" :component="componentName" />
    <slot />
  </component>
</template>

<script setup lang="ts">
import type { Component } from 'vue'
// Nuxt only auto-imports components for literal template tags, not for a JS-level reference like this
// (unlike composables, which are auto-imported anywhere via unimport); '#components' is Nuxt's own virtual
// module providing that reference explicitly.
import { NuxtLink } from '#components'
import { linkDetails } from '~/assets/link'

const props = defineProps({
  url: { type: [Object, String], required: false, default: undefined },
  rel: { type: String, required: false, default: undefined },
  target: { type: String, required: false, default: undefined, validator: (value: string) => ['_self', '_blank', '_parent', '_top'].includes(value) },
  fallbackComponent: { type: String, required: false, default: 'span' },
})

const emit = defineEmits(['click'])

const config = useRuntimeConfig()
const localePath = useLocalePath()
const rewrites = getLinkRewrites(config.public)
const currentHost = `//${useRequestURL().host}`
// `component` drives `<component :is>` and can be a native tag name ('a', 'span') or an actual resolvable
// component (NuxtLink); `componentName` is always the readable string, exposed to consumers (the
// `iconLeft` slot, the debug `data-component` attribute) so they can keep comparing against a string
// regardless of what `component` resolves to.
// shallowRef: a plain ref would deep-wrap the NuxtLink component definition assigned below in
// reactive(), which Vue explicitly warns against (breaks its internal component-identity checks).
// component.value is always replaced wholesale via setter(), never mutated in place, so shallowRef
// behaves identically for every read/write here.
const component = shallowRef<string | Component>('span')
const componentName = ref('span')
const fallbackTarget = ref(undefined)
const href = ref(undefined)
const relAdditional = ref(undefined)
const to = ref(undefined)

const setter = (values: CmsData) => {
  component.value = values.component
  componentName.value = values.componentName ?? values.component
  fallbackTarget.value = values.fallbackTarget
  href.value = values.href
  relAdditional.value = values.relAdditional
  to.value = values.to
}

const update = (target?: CmsData | string | null) => {
  if (target && typeof target === 'object') {
    target = localePath(target)
  }

  if (!target) {
    return setter({ component: props.fallbackComponent })
  }

  const { distance, scope, url, type } = linkDetails(currentHost, target, { rewrites })

  switch (scope) {
    // Use NuxtLink because this is inside the app
    case 'inside':
      return type === 'anchor'
        ? // When this is only an anchor, i.e. #foo-bar, using NuxtLink would disable the anchor behavior when that anchor is already in the url
          setter({ component: 'a', fallbackTarget: '_self', href: url })
        : // Other types can be NuxtLink. Pass the actual auto-imported component, not the string
          // 'NuxtLink': `<component :is>` resolves dynamic bindings via Vue's runtime
          // `resolveDynamicComponent`, which only finds globally `app.component()`-registered components
          // or native HTML tags. Nuxt's own built-ins (unlike this project's `global: true` components)
          // aren't registered that way, only compiled in when a literal `<NuxtLink>` tag is written in a
          // template, so a bare string here silently rendered as an inert `<nuxtlink>` custom element with
          // no navigation, instead of an `<a>`.
          // Only `to`, not `href`: NuxtLink warns when both are passed together.
          setter({ component: NuxtLink, componentName: 'NuxtLink', to: url })

    // Fallback to old-school <a href=...>
    case 'outside':
    default:
      return setter(
        distance === -1
          ? // Going to a completely different domain, i.e. from zicht.nl to example.com
            { component: 'a', fallbackTarget: '_blank', href: url, relAdditional: 'noopener' }
          : // Going to a subdomain of the same site, i.e. from zicht.nl to jobs.zicht.nl
            { component: 'a', href: url },
      )
  }
}

// Immediately set the link
update(props.url)

// Update the link whenever the url changes
watch(props, () => update(props.url))

const onClick = (event: Event) => emit('click', event)
</script>
